import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useClerkAuth';
import { useToast } from '@/hooks/use-toast';
import TopNavBar from '@/components/TopNavBar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import ArticleCreation from '@/components/ArticleCreation';
import ArticleDetailsEdit from '@/components/ArticleDetailsEdit';
import SentenceManagement from '@/components/SentenceManagement';
import VerbManagement from '@/components/VerbManagement';
import TranslationEdit from '@/components/TranslationEdit';
import { Sentence, SentenceVerb } from '@/types/article';
import { CloudinaryUploadResponse } from '@/services/cloudinary';
import {
  translateText,
  breakIntoSentences,
  identifyDifficultVerbs,
} from '@/services/openaiServer';
import { saveArticle } from '@/services/articles';
import { ArrowLeft, CheckCircle } from 'lucide-react';

const ArticleCreator = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canCreateArticle, user } = useAuth();

  // States for the creation workflow
  const [isProcessing, setIsProcessing] = useState(false);
  const [isIdentifyingVerbs, setIsIdentifyingVerbs] = useState(false);

  // Dialog states
  const [showDetailsEdit, setShowDetailsEdit] = useState(false);
  const [showSentenceConfirm, setShowSentenceConfirm] = useState(false);
  const [showVerbEdit, setShowVerbEdit] = useState(false);
  const [showTranslationEdit, setShowTranslationEdit] = useState(false);

  // Current editing states
  const [currentTitle, setCurrentTitle] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');
  const [currentOriginalLink, setCurrentOriginalLink] = useState<
    string | undefined
  >(undefined);
  const [currentImageData, setCurrentImageData] =
    useState<CloudinaryUploadResponse | null>(null);
  const [sentences, setSentences] = useState<string[]>([]);
  const [sentenceVerbs, setSentenceVerbs] = useState<{
    [index: number]: SentenceVerb[];
  }>({});
  const [translatedSentences, setTranslatedSentences] = useState<Sentence[]>(
    []
  );

  // Check permissions
  if (!canCreateArticle()) {
    navigate('/dashboard');
    return null;
  }

  const resetCreationState = () => {
    setCurrentTitle('');
    setCurrentDescription('');
    setCurrentOriginalLink(undefined);
    setCurrentImageData(null);
    setSentences([]);
    setSentenceVerbs({});
    setTranslatedSentences([]);
    setShowDetailsEdit(false);
    setShowSentenceConfirm(false);
    setShowVerbEdit(false);
    setShowTranslationEdit(false);
  };

  const handlePrepareSentences = (
    title: string,
    description: string,
    germanText: string,
    imageData: CloudinaryUploadResponse | null,
    originalLink?: string
  ) => {
    setCurrentTitle(title);
    setCurrentDescription(description);
    setCurrentOriginalLink(originalLink);
    setCurrentImageData(imageData);

    // Break text into sentences and add title as first sentence
    const sentenceTexts = !description.trim()
      ? [title.trim(), ...breakIntoSentences(germanText)]
      : [title.trim(), description.trim(), ...breakIntoSentences(germanText)];

    if (sentenceTexts.length <= 1) {
      toast({
        title: 'No sentences found',
        description:
          'Could not split text into sentences. Please check your German text.',
        variant: 'destructive',
      });
      return;
    }

    setSentences(sentenceTexts);
    setShowSentenceConfirm(true);
  };

  const handleIdentifyVerbs = async () => {
    setIsIdentifyingVerbs(true);

    // Run identification per-sentence so a single failure doesn't wipe out
    // the whole batch. Surface partial/total failures to the user rather than
    // silently producing an article with no difficult words.
    const verbsMap: { [index: number]: SentenceVerb[] } = {};
    const failedSentences: number[] = [];
    let lastError: unknown = null;

    try {
      for (let i = 0; i < sentences.length; i++) {
        try {
          const response = await identifyDifficultVerbs(
            { sentence: sentences[i] },
            user?.id
          );
          verbsMap[i] = response.verbs ?? [];
        } catch (err) {
          console.error(`Verb identification failed for sentence ${i}:`, err);
          verbsMap[i] = [];
          failedSentences.push(i);
          lastError = err;
        }
      }

      setSentenceVerbs(verbsMap);

      if (failedSentences.length === sentences.length) {
        const message =
          lastError instanceof Error ? lastError.message : 'Unknown error';
        toast({
          title: 'Verb identification failed',
          description: `Could not identify difficult words for any sentence (${message}). Please check your OpenAI key in Supabase and try again.`,
          variant: 'destructive',
        });
        return;
      }

      if (failedSentences.length > 0) {
        toast({
          title: 'Partial verb identification',
          description: `${failedSentences.length} of ${sentences.length} sentences failed. You can still review and edit before continuing.`,
          variant: 'destructive',
        });
      }

      setShowSentenceConfirm(false);
      setShowVerbEdit(true);
    } finally {
      setIsIdentifyingVerbs(false);
    }
  };

  const handleProcessArticle = async () => {
    setIsProcessing(true);

    try {
      const translatedSentences: Sentence[] = [];

      // Translate each sentence
      for (let i = 0; i < sentences.length; i++) {
        const german = sentences[i];

        // Get English and Vietnamese translations in parallel
        const [englishResponse, vietnameseResponse] = await Promise.all([
          translateText(
            {
              text: german,
              targetLanguage: 'english',
            },
            user?.id
          ),
          translateText(
            {
              text: german,
              targetLanguage: 'vietnamese',
            },
            user?.id
          ),
        ]);

        translatedSentences.push({
          id: `temp-sentence-${i + 1}`,
          order: i + 1,
          german,
          english: englishResponse.translatedText,
          vietnamese: vietnameseResponse.translatedText,
          verbs: sentenceVerbs[i] || [],
        });
      }

      setTranslatedSentences(translatedSentences);
      setShowVerbEdit(false);
      setShowSentenceConfirm(false);
      setShowTranslationEdit(true);
    } catch (error) {
      console.error('Translation error:', error);

      let errorMessage = t('admin.processingFailedDescription');

      if (error instanceof Error) {
        if (error.message.includes('OpenAI API key')) {
          errorMessage =
            'OpenAI API key not configured. Please check your environment variables.';
        } else if (error.message.includes('authenticated')) {
          errorMessage = 'You must be logged in to create articles.';
        }
      }

      toast({
        title: t('admin.processingFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveArticle = async () => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to create articles.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const articleData = {
        title: currentTitle.trim(),
        description: currentDescription.trim() || undefined,
        sentences: translatedSentences,
        imageUrl: currentImageData?.secure_url,
        imagePublicId: currentImageData?.public_id,
        originalLink: currentOriginalLink,
      };

      const savedArticle = await saveArticle(user.id, articleData);

      toast({
        title: 'Article Created Successfully!',
        description: `Article "${savedArticle.title}" has been created with ${translatedSentences.length} sentences.`,
      });

      // Reset and navigate
      resetCreationState();
      navigate('/manage-articles');
    } catch (error) {
      console.error('Article saving error:', error);

      let errorMessage = t('admin.processingFailedDescription');

      if (error instanceof Error) {
        if (error.message.includes('authenticated')) {
          errorMessage = 'You must be logged in to create articles.';
        }
      }

      toast({
        title: t('admin.processingFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleDialogClose = (dialogType: string, open: boolean) => {
    if (!open) {
      switch (dialogType) {
        case 'details':
          setShowDetailsEdit(false);
          break;
        case 'sentences':
          setShowSentenceConfirm(false);
          break;
        case 'verbs':
          setShowVerbEdit(false);
          break;
        case 'translation':
          setShowTranslationEdit(false);
          break;
      }
    }
  };

  return (
    <>
      <TopNavBar
        currentPage="create"
        onNavigateToAdmin={() => navigate('/?view=admin')}
        onNavigateToArticles={() => navigate('/')}
        onNavigateToDashboard={() => navigate('/dashboard')}
        onNavigateToQuestions={() => navigate('/questions')}
      />
      <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 p-6">
        <div className="container max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="bg-white/10 border-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>

            <div className="bg-gradient-hero rounded-lg p-6 text-primary-foreground mb-6">
              <h1 className="text-3xl font-bold mb-2">
                {t('articleCreator.title')}
              </h1>
              <p className="text-primary-foreground/90">
                Create a new German article with AI-powered translations and
                verb identification
              </p>
            </div>
          </div>

          {/* Creation Progress Indicator */}
          <Card className="mb-6 bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Creation Progress
              </CardTitle>
              <CardDescription>
                Follow the steps to create your German article
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm">
                <div
                  className={`flex items-center gap-2 ${sentences.length > 0 ? 'text-green-600' : 'text-muted-foreground'}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${sentences.length > 0 ? 'bg-green-600' : 'bg-muted-foreground'}`}
                  />
                  Article Content
                </div>
                <div
                  className={`flex items-center gap-2 ${Object.keys(sentenceVerbs).length > 0 ? 'text-green-600' : 'text-muted-foreground'}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${Object.keys(sentenceVerbs).length > 0 ? 'bg-green-600' : 'bg-muted-foreground'}`}
                  />
                  Verb Identification
                </div>
                <div
                  className={`flex items-center gap-2 ${translatedSentences.length > 0 ? 'text-green-600' : 'text-muted-foreground'}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${translatedSentences.length > 0 ? 'bg-green-600' : 'bg-muted-foreground'}`}
                  />
                  Translation
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Article Creation Form */}
          <div className="mb-8">
            <ArticleCreation
              onPrepareSentences={handlePrepareSentences}
              isProcessing={isProcessing}
            />
          </div>

          {/* Dialogs */}
          <ArticleDetailsEdit
            open={showDetailsEdit}
            onOpenChange={open => handleDialogClose('details', open)}
            title={currentTitle}
            description={currentDescription}
            originalLink={currentOriginalLink}
            imageData={currentImageData}
            onTitleChange={setCurrentTitle}
            onDescriptionChange={setCurrentDescription}
            onOriginalLinkChange={setCurrentOriginalLink}
            onImageDataChange={setCurrentImageData}
            onContinueToSentences={() => {
              setShowDetailsEdit(false);
              setShowSentenceConfirm(true);
            }}
            isEditingExistingArticle={false}
          />

          <SentenceManagement
            open={showSentenceConfirm}
            onOpenChange={open => handleDialogClose('sentences', open)}
            sentences={sentences}
            onSentencesChange={setSentences}
            onIdentifyVerbs={handleIdentifyVerbs}
            onProcessArticle={handleProcessArticle}
            onEditVerbs={() => {
              setShowSentenceConfirm(false);
              setShowVerbEdit(true);
            }}
            isProcessing={isProcessing}
            isIdentifyingVerbs={isIdentifyingVerbs}
            isEditingExistingArticle={false}
          />

          <VerbManagement
            open={showVerbEdit}
            onOpenChange={open => handleDialogClose('verbs', open)}
            sentences={sentences}
            sentenceVerbs={sentenceVerbs}
            onSentenceVerbsChange={setSentenceVerbs}
            onProcessArticle={handleProcessArticle}
            isProcessing={isProcessing}
          />

          <TranslationEdit
            open={showTranslationEdit}
            onOpenChange={open => handleDialogClose('translation', open)}
            translatedSentences={translatedSentences}
            onTranslatedSentencesChange={setTranslatedSentences}
            onSave={handleSaveArticle}
            isEditingExistingArticle={false}
          />
        </div>
      </div>
    </>
  );
};

export default ArticleCreator;
